<template>
  <div class="route-map-container">
    <div :style="{ height: height }" class="route-map">
      <LMap :zoom="zoom" :center="mapCenter" :options="mapOptions">
        <LTileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />

        <!-- Origin Marker -->
        <LMarker
          :lat-lng="[origin.latitude, origin.longitude]"
          :icon="originIcon"
        >
          <LPopup>
            <div class="popup-content">
              <h4 class="font-bold text-green-600">🏭 EPOD Location</h4>
              <p class="text-sm" v-if="origin.location_address">
                <strong>Address:</strong> {{ origin.location_address }}
              </p>
              <p class="text-sm" v-if="driverName">
                <strong>driver:</strong> {{ driverName }}
              </p>
              <p class="text-sm" v-if="vehicleNumber">
                <strong>vehicle:</strong> {{ vehicleNumber }}
              </p>
              <p class="text-xs">
                <strong>Coordinates:</strong> {{ origin.latitude }},
                {{ origin.longitude }}
              </p>
            </div>
          </LPopup>
        </LMarker>

        <!-- Destination Marker -->
        <LMarker
          :lat-lng="[destination.latitude, destination.longitude]"
          :icon="destinationIcon"
        >
          <LPopup>
            <div class="popup-content">
              <h4 class="font-bold text-red-600">📍 Customer Location</h4>
              <p class="text-sm">
                <strong>Address:</strong> {{ destination.location_address }}
              </p>
              <p class="text-xs">
                <strong>Coordinates:</strong> {{ destination.latitude }},
                {{ destination.longitude }}
              </p>
            </div>
          </LPopup>
        </LMarker>

        <!-- Route Polyline -->
        <LPolyline
          :lat-lngs="routeCoordinates"
          :color="routeColor"
          :weight="routeWeight"
          :opacity="0.8"
          :dashArray="routeDashArray"
        />

        <!-- Distance and Duration Info -->
        <LControl position="topright">
          <div class="route-info bg-white p-3 rounded shadow-lg border">
            <h5 class="font-bold text-gray-800 mb-2">
              📊 Delivery Information
            </h5>
            <div class="text-sm space-y-1">
              <p v-if="customerLocation && customerLocation.latitude">
                <span class="font-medium">📍 Lat Customer:</span>
                {{ customerLocation.latitude }}
              </p>
              <p v-if="customerLocation && customerLocation.longitude">
                <span class="font-medium">📍 Long Customer:</span>
                {{ customerLocation.longitude }}
              </p>
              <p>
                <span class="font-medium">🟢 Lat Delivery:</span>
                {{ origin.latitude }}
              </p>
              <p>
                <span class="font-medium">🟢 Long Delivery:</span>
                {{ origin.longitude }}
              </p>
              <p>
                <span class="font-medium">📏 Distance:</span>
                <span v-if="distanceBetweenCustomerAndVisit">
                  {{ distanceBetweenCustomerAndVisit }}
                </span>
                <span v-else>
                  {{ routeDistance }} km
                  <span class="text-xs text-gray-500"
                    >(Origin-Destination)</span
                  >
                </span>
              </p>
              <p v-if="checkOutTime">
                <span class="font-medium">🚪 Check Out:</span>
                {{ formatTime(checkOutTime) }}
              </p>
              <p v-if="destination.location_timestamp">
                <span class="font-medium">⏱️ Timestamp:</span>
                {{ formatTime(destination.location_timestamp) }}
              </p>
              <template v-if="isEndVisitPending">
                <p>
                  <span class="font-medium">🚪 Check Out:</span>
                  -
                </p>
                <p class="route-info-note">
                  ⏳ Driver belum End Visit. Lokasi ditampilkan dari koordinat
                  warehouse.
                </p>
              </template>
            </div>
          </div>
        </LControl>
      </LMap>
    </div>
  </div>
</template>

<script>
import {
  LMap,
  LTileLayer,
  LMarker,
  LPopup,
  LPolyline,
  LControl,
} from "vue2-leaflet";
import L from "leaflet";
import moment from "moment";

export default {
  name: "RouteMapViewer",
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LPolyline,
    LControl,
  },
  props: {
    origin: {
      type: Object,
      required: true,
      validator: (obj) => obj.latitude && obj.longitude,
    },
    destination: {
      type: Object,
      required: true,
      validator: (obj) => obj.latitude && obj.longitude,
    },
    customerLocation: {
      type: Object,
      default: () => ({
        latitude: null,
        longitude: null,
      }),
    },
    height: {
      type: String,
      default: "400px",
    },
    routeColor: {
      type: String,
      default: "#3b82f6", // Blue color
    },
    routeWeight: {
      type: Number,
      default: 4,
    },
    showDirectRoute: {
      type: Boolean,
      default: true, // If false, could integrate with routing API later
    },
    driverName: {
      type: String,
      default: null,
    },
    vehicleNumber: {
      type: String,
      default: null,
    },
    // True jika driver ePOD belum end visit: tampilkan Check Out kosong
    // dan keterangan bahwa lokasi diambil dari koordinat warehouse
    isEndVisitPending: {
      type: Boolean,
      default: false,
    },
    // Waktu driver check out dari lokasi customer (end_visit_time)
    checkOutTime: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      zoom: 13,
      mapOptions: {
        zoomControl: true,
        attributionControl: true,
        scrollWheelZoom: true,
      },
      routeDashArray: "10, 5", // Dashed line for direct route
    };
  },
  computed: {
    mapCenter() {
      // Center map between origin and destination
      const centerLat = (this.origin.latitude + this.destination.latitude) / 2;
      const centerLng =
        (this.origin.longitude + this.destination.longitude) / 2;
      return [centerLat, centerLng];
    },

    originIcon() {
      return L.divIcon({
        html: `<div style="
          background: #10b981; 
          color: white; 
          border-radius: 50%; 
          width: 30px; 
          height: 30px; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          font-weight: bold;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ">🏭</div>`,
        className: "custom-origin-icon",
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });
    },

    destinationIcon() {
      return L.divIcon({
        html: `<div style="
          background: #ef4444; 
          color: white; 
          border-radius: 50%; 
          width: 30px; 
          height: 30px; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          font-weight: bold;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ">📍</div>`,
        className: "custom-destination-icon",
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });
    },

    routeCoordinates() {
      if (this.showDirectRoute) {
        // Simple direct line between two points
        return [
          [this.origin.latitude, this.origin.longitude],
          [this.destination.latitude, this.destination.longitude],
        ];
      }
      // Here you could integrate with routing API like OpenRouteService, Mapbox, etc.
      return [];
    },

    routeDistance() {
      // Calculate distance using Haversine formula
      const R = 6371; // Earth's radius in kilometers
      const dLat = this.deg2rad(
        this.destination.latitude - this.origin.latitude
      );
      const dLon = this.deg2rad(
        this.destination.longitude - this.origin.longitude
      );
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.deg2rad(this.origin.latitude)) *
          Math.cos(this.deg2rad(this.destination.latitude)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;
      return distance.toFixed(2);
    },

    // Jarak antara customer location dan visit location
    distanceBetweenCustomerAndVisit() {
      if (
        !this.customerLocation ||
        !this.customerLocation.latitude ||
        !this.customerLocation.longitude
      ) {
        return null;
      }

      const R = 6371; // Earth's radius in kilometers
      const dLat = this.deg2rad(
        this.origin.latitude - this.customerLocation.latitude
      );
      const dLon = this.deg2rad(
        this.origin.longitude - this.customerLocation.longitude
      );
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.deg2rad(this.customerLocation.latitude)) *
          Math.cos(this.deg2rad(this.origin.latitude)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;

      // Return dalam meter jika < 1km, kilometer jika >= 1km
      if (distance < 1) {
        return `${(distance * 1000).toFixed(2)} m`;
      }
      return `${distance.toFixed(2)} km`;
    },

    routeDuration() {
      // Estimate duration based on average city speed (30 km/h)
      const avgSpeed = 30; // km/h
      const duration = (parseFloat(this.routeDistance) / avgSpeed) * 60;
      return Math.round(duration);
    },
  },
  methods: {
    formatTime(timestamp) {
      return moment(timestamp).format("DD MMM YYYY HH:mm:ss");
    },

    deg2rad(deg) {
      return deg * (Math.PI / 180);
    },
  },
  mounted() {
    // Auto-fit map to show both points
    this.$nextTick(() => {
      if (this.$children[0] && this.$children[0].mapObject) {
        const map = this.$children[0].mapObject;
        const bounds = L.latLngBounds([
          [this.origin.latitude, this.origin.longitude],
          [this.destination.latitude, this.destination.longitude],
        ]);
        map.fitBounds(bounds, { padding: [20, 20] });
      }
    });
  },
};
</script>

<style scoped>
.route-map-container {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.route-map {
  width: 100%;
}

.popup-content {
  max-width: 250px;
}

.route-info {
  min-width: 200px;
}

.route-info-note {
  max-width: 240px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
  font-size: 12px;
  line-height: 1.4;
  color: #b45309;
  white-space: normal;
}

/* Custom icon styles */
.custom-origin-icon,
.custom-destination-icon {
  background: none !important;
  border: none !important;
}
</style>
